import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

import { AD_LAYOUT_STUBS, PRODUCTION_ORIGIN } from "@consts";
import {
	getDateDataFromDateCode,
	isBeforeMadridCutoff,
	getExpiresHeaderForNextMadridCutoff,
} from "@utils";

import wordleMixin from "@/plugins/mixins/wordle";
import { getCMSCopy } from "~/common/services/cmsCopyService";

dayjs.extend(utc);
dayjs.extend(tz);

export default {
	name: "Parseword",
	mixins: [wordleMixin],
	async fetch() {
		const cmsCopyKey = ".games.parseword";
		const cmsCopy = await getCMSCopy(
			this.$axios,
			this.$config,
			cmsCopyKey,
			process.env.version
		);

		this.bodyCopy = cmsCopy.data.body;

		this.isLoading = true;
		if (process.client) {
			this.loadData()
				.catch((err) => {
					console.error("Parseword loadData failed", err);
				})
				.finally(() => {
					this.isLoading = false;
				});
		} else if (process.server) {
			this.serverDateParam = this.getResolvedDateParam();
			await this.loadData();
		}
	},
	mounted() {
		if (this.serverDateParam) {
			console.warn(
				"PARSEWORD DEBUG >>> found a serverDateParam value",
				this.serverDateParam
			);
			const clientDateParam = this.getResolvedDateParam();
			console.warn(
				"PARSEWORD DEBUG >>> creating client time",
				clientDateParam
			);
			if (clientDateParam !== this.serverDateParam) {
				console.warn(
					"PARSEWORD DEBUG >>> client time and server time mismatch",
					{ client: clientDateParam, server: this.serverDateParam }
				);
				this.isLoading = true;
				this.loadData()
					.catch((err) => {
						console.error("Parseword loadData failed", err);
					})
					.finally(() => {
						this.isLoading = false;
					});
			} else {
				this.isLoading = false;
			}
		}
	},
	head({ $buildMetaDataObject }) {
		const title = `Parseword Hints, Clues & Answer for Today (${this.currentDateString?.replaceAll(
			"&nbsp;",
			" "
		)}) - #${this.puzzleIndex}`;

		const description =
			"Need help with today's Parseword? Get hints, clues, and the answer to solve Josh Wardle's daily cryptic crossword puzzle without spoiling the fun. WordFinder has your back every day.";

		const metaData = $buildMetaDataObject({
			title,
			description,
		});

		metaData.script = metaData.script || [];

		const dateParam = this.getResolvedDateParam();
		const dateModified = dayjs.tz(dateParam, "UTC");

		metaData.script.push({
			hid: "jsonld-parseword-hints",
			type: "application/ld+json",
			json: {
				"@context": "https://schema.org",
				"@type": "NewsArticle",
				headline: title,
				datePublished: "2026-04-07T00:00:00.000Z",
				dateModified: dateModified.toISOString(),
				author: [
					{
						"@type": "Person",
						name: "Kate Miller-Wilson",
						url: `${PRODUCTION_ORIGIN}/about/`,
					},
				],
			},
		});

		return metaData;
	},
	asyncData({ res }) {
		if (res) {
			res.setHeader("Expires", getExpiresHeaderForNextMadridCutoff());
		}
	},
	data() {
		return {
			serverDateParam: null,
			AD_LAYOUT_STUBS,
			apiData: null,
			currentData: null,
			pastData: [],
			hints: [],
			isLoading: true,
			bodyCopy: "",
		};
	},
	computed: {
		h1Text() {
			return `Parseword Hints & Answer for Today (${this.currentDateString})&nbsp;-&nbsp;#${this.puzzleIndex}`;
		},
		answerSectionData() {
			return {
				answer: this.currentData.solution,
				date: this.datePreparedForFormatting,
				index: this.puzzleIndex,
			};
		},
		subHeading() {
			if (!this.currentData) {
				return "";
			}

			return `
				Today’s&nbsp;Parseword&nbsp;Hints ${this.currentDateString}&nbsp;- #${this.puzzleIndex}`;
		},
		currentDateString() {
			if (!this.currentData) {
				return null;
			}

			const date = getDateDataFromDateCode(
				this.datePreparedForFormatting,
				this.isSmallDevice
			);

			return `${date.month}&nbsp;${date.day},&nbsp;${date.year}`;
		},
		puzzleIndex() {
			return this.getPuzzleIndexForDate(this.currentData?.date);
		},
		pastPuzzlesGrouped() {
			if (!Array.isArray(this.pastData) || !this.pastData.length) {
				return [];
			}

			const byMonth = new Map();

			for (const puzzle of this.pastData) {
				const iso = puzzle?.date;
				if (!iso || typeof iso !== "string") {
					continue;
				}
				const key = iso.slice(0, 7);
				if (!byMonth.has(key)) {
					byMonth.set(key, []);
				}
				byMonth.get(key).push(puzzle);
			}

			const monthKeys = [...byMonth.keys()].sort((a, b) =>
				b.localeCompare(a)
			);

			return monthKeys.map((monthKey) => {
				const [y, m] = monthKey.split("-");
				const dateCodeYyMm = `${y.slice(2)}${m}`;
				const periodInfo = getDateDataFromDateCode(dateCodeYyMm, false);

				const puzzles = byMonth
					.get(monthKey)
					.slice()
					.sort((a, b) => b.date.localeCompare(a.date))
					.map((p) => {
						const dateCode = p.date.replace(/-/g, "").slice(2);
						const d = getDateDataFromDateCode(
							dateCode,
							this.isSmallDevice
						);
						const idx = this.getPuzzleIndexForDate(p.date);
						const metaLine = `${d.month} ${parseInt(d.day, 10)}, ${
							d.year
						} #${idx}`;

						return {
							metaLine,
							solution: String(p.solution || "").toUpperCase(),
						};
					});

				return {
					monthKey,
					period: periodInfo.formatted,
					puzzles,
				};
			});
		},
		datePreparedForFormatting() {
			return this.currentData.date.replace(/-/g, "").slice(2);
		},
	},
	methods: {
		getPuzzleIndexForDate(dateStr) {
			if (!dateStr) {
				return "";
			}

			const BASE_DATE = "2026-03-10";
			const BASE_INDEX_OFFSET = 41;

			const date = new Date(`${dateStr}T00:00:00Z`);
			const baseDate = new Date(`${BASE_DATE}T00:00:00Z`);

			const MS_PER_DAY = 24 * 60 * 60 * 1000;
			const diffDays = Math.round((date - baseDate) / MS_PER_DAY);

			return diffDays + BASE_INDEX_OFFSET;
		},
		getResolvedDateParam() {
			return this.getDateParam(isBeforeMadridCutoff());
		},
		async loadData() {
			const params = {
				date: this.getResolvedDateParam(),
				past: "true",
			};

			const response = await this.$axios.get(
				`${this.$config.API_SERVICE}/v4/parseword`,
				{ params }
			);

			if (response.status !== 200) {
				throw new Error(`Unexpected status code: ${response.status}`);
			}

			if (response.data.current) {
				this.currentData = response.data.current;
			} else {
				throw new Error("Missing current data");
			}

			if (response.data.past) {
				this.pastData = response.data.past;
			} else {
				throw new Error("Missing past data");
			}
		},
	},
};
